/**
 * Voice Status Bar Component
 *
 * Persistent bar at bottom of screen showing voice status when connected
 * Supports both channel voice and voice rooms
 */

import React from 'react';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useChannel } from '@/hooks/useChannels';
import { useVoiceRooms } from '@/hooks/useVoiceRooms';

export const VoiceStatusBar: React.FC = () => {
  const { state, toggleMute, toggleDeafen, leaveChannelVoice, leaveVoiceRoom } = useLiveKit();
  const { data: channel } = useChannel(state.channelId ?? null);
  const { data: voiceRooms } = useVoiceRooms();

  // Don't render if not connected
  if (!state.isConnected || (!state.channelId && !state.voiceRoomId)) {
    return null;
  }

  const isVoiceRoom = !!state.voiceRoomId;
  const voiceRoom = isVoiceRoom ? voiceRooms?.find(r => r.id === state.voiceRoomId) : null;
  const displayName = isVoiceRoom ? (voiceRoom?.name || 'Стая') : (channel?.name || 'Unknown');
  const label = isVoiceRoom ? 'Гласова стая' : 'Гласов канал';
  const handleLeave = isVoiceRoom ? leaveVoiceRoom : leaveChannelVoice;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-green-700 border-t border-green-600 px-4 py-2 flex items-center justify-between z-40 shadow-lg">
      {/* Left: Info */}
      <div className="flex items-center space-x-3">
        <svg className="w-5 h-5 text-green-100" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 3.75a2 2 0 10-4 0v6a2 2 0 104 0v-6zM17.937 9.75a.75.75 0 01-.75.75h-.214c-.346 2.556-2.472 4.5-5.023 4.5a5.25 5.25 0 01-5.023-4.5h-.214a.75.75 0 010-1.5h.214a5.25 5.25 0 015.023-4.5c2.551 0 4.677 1.944 5.023 4.5h.214a.75.75 0 01.75.75zM10 13.5a2.5 2.5 0 002.5-2.5v-1.5a2.5 2.5 0 10-5 0v1.5a2.5 2.5 0 002.5 2.5z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-white">
            {label}: {displayName}
          </span>
          <span className="text-xs text-green-100">
            {state.participants.length} {state.participants.length === 1 ? 'участник' : 'участници'}
          </span>
        </div>
      </div>

      {/* Right: Controls */}
      <div className="flex items-center space-x-2">
        <div className="flex items-center space-x-2 px-3 py-1 bg-green-800 rounded-lg">
          {state.isConnecting && (
            <>
              <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
              <span className="text-xs text-yellow-100">Свързване...</span>
            </>
          )}
          {state.isConnected && !state.isConnecting && (
            <>
              <span className="inline-block w-2 h-2 bg-white rounded-full" />
              <span className="text-xs text-white font-medium">Свързан</span>
            </>
          )}
        </div>

        <button
          onClick={toggleMute}
          disabled={state.isDeafened}
          className={`p-2 rounded-lg transition-colors ${
            state.isMuted ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-800 hover:bg-green-900 text-green-100'
          } ${state.isDeafened ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={state.isMuted ? 'Включи микрофон' : 'Изключи микрофон'}
        >
          <span className="text-lg">{state.isMuted ? '🔇' : '🎤'}</span>
        </button>

        <button
          onClick={toggleDeafen}
          className={`p-2 rounded-lg transition-colors ${
            state.isDeafened ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-800 hover:bg-green-900 text-green-100'
          }`}
          title={state.isDeafened ? 'Включи звука' : 'Изключи звука'}
        >
          <span className="text-lg">{state.isDeafened ? '🔕' : '🔔'}</span>
        </button>

        {state.isScreenSharing && (
          <div className="px-3 py-1 bg-blue-600 rounded-lg flex items-center space-x-2">
            <span className="text-lg">🖥️</span>
            <span className="text-xs text-white">Споделяне</span>
          </div>
        )}

        <button
          onClick={handleLeave}
          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium text-sm"
          title="Напусни"
        >
          Напусни
        </button>
      </div>
    </div>
  );
};
